import { container } from 'tsyringe'
import {
  BODY_LABEL_ADDON_ANIMATION,
  BODY_LABEL_ADDON_FROM_SLIT,
  PhysicsLayerZ,
  MATTER_TO_THREE_SCALE,
  TEST_ESCAPED_COINS_INTERVAL,
  BODY_LABEL_EPHEMERAL_TOOL
} from '@/features/experience/misc/constants'
import { Body, Bounds, Engine, Render, World } from 'matter-js'
import { Line3, Mesh, PerspectiveCamera, Vector2, Vector3 } from 'three'
import SceneUtils from '../misc/scene-utils'
import RoomPhysicsInteractions from './room-physics-interactions'
import { DebugFlag } from '@/common/constants'
import { Interface } from '@/shared/di/interface'
import type { IDebug } from '@/shared/di/interface/debug'
import type { ISize } from '@/shared/di/interface/size'
import RoomBase, { RoomConfig } from './room-base'
import Action from '@/common/action'

const tempVector3 = new Vector3()
const tempVector3_2 = new Vector3()
const tempVector2 = new Vector2()

export enum BodyType {
  Coin = 'coin',
  Slit = 'slit',
  Collider = 'collider',
  Action = 'action',
  Unknown = 'unknown'
}

export enum CollisionGroup {
  NoCoins = 0,
  InactiveCoins = 1,
  ActiveCoins = 2,
  UILayer = 3
}

export enum CollisionCategory {
  Coins = 1,
  StaticWallsBack = 2,
  StaticWallsFront = 4,
  SlitBlockers = 16,
  UILayer = 32,
  None = 256
}

export interface MeshBodyPair {
  mesh: Mesh
  body: Body
}

interface RendererOptions {
  width?: number
  height?: number
  hasBounds?: boolean
  wireframes?: boolean
  background?: string
  wireframeBackground?: string
  showSleeping?: boolean
  showPositions?: boolean
  showAngleIndicator?: boolean
  showBounds?: boolean
}

export default abstract class RoomPhysicsBase {
  onSwitchSensorOn = new Action<string>()
  onSwitchSensorOff = new Action<string>()
  isActive = false
  #accumulator = 0
  #renderCanvas: HTMLCanvasElement | undefined
  #room: RoomBase | undefined
  engine: Engine
  camera: PerspectiveCamera = {} as PerspectiveCamera
  interactions: RoomPhysicsInteractions
  render: Render = {} as Render
  visibleEdge = new Vector2()
  bodies: Body[] = []
  colliders: Body[] = []
  slitSensors: Body[] = []
  switchSensors: Body[] = []
  #switchSensorStatus = new Map<string, boolean>()

  #lastTimeCheckedEscapedCoins = Date.now()
  #escapeCoinBounds = {
    minY: -3000,
    maxY: 3000
  }

  particleSpawnDistance = 4
  globalClosestDistanceToSlit = 10000

  constructor() {
    this.engine = Engine.create(undefined)
    this.engine.world.gravity.y = 0
    this.engine.world.gravity.x = 0

    this.interactions = new RoomPhysicsInteractions(this)
  }

  setRoom(room: RoomBase) {
    this.#room = room
  }

  getRoomConfig(): RoomConfig {
    if (this.#room) {
      return this.#room.config
    } else {
      return {} as RoomConfig
    }
  }

  setCamera(camera: PerspectiveCamera) {
    this.camera = camera
    this.interactions.setCamera(camera)
  }

  init() {
    if (container.resolve<IDebug>(Interface.IDebug).getFlag(DebugFlag.Physics)) {
      this.#renderCanvas = document.createElement('canvas') as HTMLCanvasElement

      this.#renderCanvas.width = 200
      this.#renderCanvas.height = (200 * 16) / 9
      this.#renderCanvas.style.position = 'absolute'
      this.#renderCanvas.style.top = '70px'
      this.#renderCanvas.style.left = '20px'
      this.#renderCanvas.style.zIndex = '1000'
      this.#renderCanvas.style.borderRadius = '10px'
      this.#renderCanvas.style.pointerEvents = 'none'
      this.#renderCanvas.style.opacity = '0.8'
      const ctx = this.#renderCanvas.getContext('2d')
      ctx?.fillRect(0, 0, this.#renderCanvas.width, this.#renderCanvas.height)
      document.body.appendChild(this.#renderCanvas)

      this.initRender()
    }

    this.colliders.push(...this.createSideWalls())
  }

  initRender() {
    const renderOptions: RendererOptions = {
      background: '#222222',
      wireframeBackground: '#222222',
      wireframes: false,
      showPositions: true,
      showAngleIndicator: true
    }
    this.render = Render.create({
      engine: this.engine,
      canvas: this.#renderCanvas,
      options: renderOptions
    })
  }

  // return an array with bodies to add to colliders list
  createSideWalls(): Body[] {
    return []
  }

  updateLeftSlitColliders() {}
  updateRightSlitColliders() {}
  enableTopSensor() {}
  disableTopSensor() {}

  addCollider(body: Body) {
    this.colliders.push(body)

    if (body.label.includes('slit')) {
      this.slitSensors.push(body)
    } else if (body.label.includes('switch')) {
      this.switchSensors.push(body)
    }

    World.add(this.engine.world, [body])
  }

  resetDistanceToSlits() {
    this.globalClosestDistanceToSlit = 100000
  }

  distanceToNearestDraggedCoinFrom(line: Line3) {
    let minDistance = 10000
    this.interactions.draggedCoins.forEach(body => {
      if (!SceneUtils.isCoin(body)) {
        return
      }

      const bodyPosition = SceneUtils.matterToSceneAtZ0(body.position as Vector2)
      bodyPosition.z = PhysicsLayerZ.Front

      const pointOnLine = new Vector3()

      line.closestPointToPoint(bodyPosition, true, pointOnLine)
      const dist = pointOnLine.distanceTo(bodyPosition)

      if (dist < this.particleSpawnDistance) {
        const velocity = pointOnLine.clone().sub(bodyPosition).normalize().negate().multiplyScalar(2)
        velocity.y += Math.random() - 0.5
        velocity.x += Math.random() - 0.5
        velocity.z = 0

        if (this.#room && this.#room.particleManager) {
          this.#room.particleManager.spawnParticle({
            position: bodyPosition,
            velocity: velocity.clone().negate(),
            size: 2.5,
            lifetime: 1,
            velocityRandomness: 0,
            positionRandomness: 0.1,
            turbulence: 0.2
          })

          if (Math.random() > 0.8) {
            bodyPosition.z = PhysicsLayerZ.Front
            this.#room.particleManager.spawnParticle({
              position: pointOnLine,
              velocity: velocity,
              size: 2.5,
              lifetime: 1,
              velocityRandomness: 0,
              positionRandomness: 0.1,
              turbulence: 0.2
            })
          }
        }
      }

      minDistance = Math.min(dist, minDistance)
    })

    this.globalClosestDistanceToSlit = Math.min(this.globalClosestDistanceToSlit, minDistance)

    return minDistance
  }

  activate() {
    this.isActive = true
    if (this.#renderCanvas) {
      Render.run(this.render)
      this.updateRenderViewport()
      if (this.#renderCanvas) {
        this.#renderCanvas.style.display = 'block'
      }
    }

    this.updateEscapeBounds()
    this.interactions.activate()
  }

  inactivate() {
    this.isActive = false

    if (this.#renderCanvas) {
      Render.stop(this.render)
      this.#renderCanvas.style.display = 'none'
    }

    this.interactions.inactivate()
  }

  destroy() {
    if (this.#renderCanvas) {
      this.#renderCanvas.remove()
    }
    this.interactions.destroy()
    this.#room = undefined
    this.#switchSensorStatus.clear()

    //TODO: remove all colliders
  }

  add(body: Body) {
    this.bodies.push(body)

    World.add(this.engine.world, body)
  }

  remove(body: Body) {
    if (this.bodies.includes(body)) {
      this.bodies.splice(
        this.bodies.findIndex(x => x === body),
        1
      )
    }

    World.remove(this.engine.world, body)
  }

  removeSwitchColliders() {
    this.onSwitchSensorOn.removeAll()
    this.onSwitchSensorOff.removeAll()
    this.switchSensors.forEach(b => {
      World.remove(this.engine.world, b)
    })
  }

  update(time: number, delta: number) {
    this.forceCoinsIntoView()
    if (!this.isActive) {
      return
    }

    let finalDelta = delta

    const timeStep = 0.016
    const maxUpdatesPerFrame = 6
    const maxDeltaTime = timeStep * maxUpdatesPerFrame

    if (delta > maxDeltaTime) {
      // console.warn('exceeding max deltatime ('+maxDeltaTime+'): '+deltaTime)
      finalDelta = maxDeltaTime
    }

    this.#accumulator += finalDelta

    this.interactions.earlyUpdate(time, 0)

    //run several time-steps if low framerate
    while (this.isActive && this.#accumulator > timeStep) {
      this.#accumulator -= timeStep
      this.interactions.update(time, timeStep)
      Engine.update(this.engine, timeStep * 1000)
    }

    //if fast framerate, execute whatever is left in #accumulator
    /*if (this.#accumulator > 0) {
      this.interactions.update(time, this.#accumulator)
      Engine.update(this.engine, this.#accumulator * 1000)
      this.#accumulator = 0
    }*/

    this.interactions.lateUpdate(time, this.#accumulator)

    this.testSwitchSensors()
    this.checkForEscapedCoins()
    this.applyGravity()
  }

  forceCoinsIntoView() {
    this.bodies
      .filter(
        b =>
          b.collisionFilter.group === CollisionGroup.InactiveCoins || b.collisionFilter.group === CollisionGroup.UILayer
      )
      .forEach(body => {
        if (body.position.x < this.visibleEdge.x * 0.8) {
          Body.applyForce(body, body.position, { x: body.mass * 0.002, y: 0 })
        } else if (body.position.x > -this.visibleEdge.x * 0.8) {
          Body.applyForce(body, body.position, { x: body.mass * -0.002, y: 0 })
        }

        if (body.position.y < this.visibleEdge.y * 0.9) {
          Body.applyForce(body, body.position, { x: 0, y: body.mass * 0.002 })
        } else if (body.position.y > 20) {
          Body.applyForce(body, body.position, { x: 0, y: body.mass * -0.002 })
        }
      })
  }

  applyGravity() {
    this.bodies
      .filter(
        b =>
          b.plugin &&
          b.plugin.gravity &&
          b.plugin.gravity > 0 &&
          (b.collisionFilter.group === CollisionGroup.ActiveCoins || b.collisionFilter.group === CollisionGroup.UILayer)
      )
      .forEach(sourceBody => {
        this.bodies
          .filter(
            b =>
              !b.label.includes(BODY_LABEL_EPHEMERAL_TOOL) &&
              !b.label.includes(BODY_LABEL_ADDON_ANIMATION) &&
              b.plugin.gravityParentId === sourceBody.id
          )
          .forEach(targetBody => {
            Body.setVelocity(targetBody, { x: 0, y: 0 })
            tempVector2
              .copy(sourceBody.position as Vector2)
              .sub(targetBody.position as Vector2)
              .multiplyScalar(0.003)

            Body.applyForce(targetBody, sourceBody.position, tempVector2)

            tempVector3.set(
              sourceBody.position.x * MATTER_TO_THREE_SCALE,
              -sourceBody.position.y * MATTER_TO_THREE_SCALE,
              3
            )
            tempVector3_2.set(
              targetBody.position.x * MATTER_TO_THREE_SCALE,
              -targetBody.position.y * MATTER_TO_THREE_SCALE,
              0
            )
            const dist = Math.random()
            tempVector3.lerp(tempVector3_2, dist)

            //tempVector3.x += Math.random() * dist
            //tempVector3.y += Math.random() * dist

            tempVector3_2.set(0, 0, 0)

            this.#room?.particleManager?.spawnParticle({
              position: tempVector3,
              velocity: tempVector3_2,
              size: 7 - 5 * dist,
              opacity: dist,
              lifetime: 0.5,
              velocityRandomness: dist > 0.8 ? 1 : 0,
              positionRandomness: 0,
              turbulence: 0
            })
          })
      })
  }

  checkForEscapedCoins() {
    if (!this.#room) {
      return
    }

    //todo: check if coins have moved only

    const now = Date.now()
    if (this.#lastTimeCheckedEscapedCoins + TEST_ESCAPED_COINS_INTERVAL < now) {
      this.#lastTimeCheckedEscapedCoins = now

      this.bodies
        .filter(b => !this.interactions.draggedCoins.includes(b))
        .forEach(body => {
          if (body.label.includes(BODY_LABEL_ADDON_ANIMATION) || body.label.includes(BODY_LABEL_ADDON_FROM_SLIT)) {
            return
          }

          if (body.position.y < this.#escapeCoinBounds.minY || body.position.y > this.#escapeCoinBounds.maxY) {
            const controller = this.#room!.getTokenControllerByBodyId(body.id)
            if (controller) {
              this.#room!.placeCoin(controller, true)
              controller.animateInBody()
            }
          }
        })
    }
  }

  updateRenderViewport() {
    const width = Math.abs(this.visibleEdge.x * 2) * 1.3
    const height = Math.abs(this.visibleEdge.y * 2)

    const scaleX = this.render.canvas.width / width
    const scaleY = this.render.canvas.height / height

    this.render.context.setTransform(
      scaleX,
      0,
      0,
      scaleX,
      -this.visibleEdge.x * scaleX * 1.25,
      -this.visibleEdge.y * scaleY * 1.7
    )
  }

  updateEscapeBounds() {
    //update escape coin bounds
    const bottomCollider = this.colliders.find(x => x.label.toLowerCase().includes('bottom'))
    if (bottomCollider) {
      this.#escapeCoinBounds.maxY = bottomCollider.bounds.max.y
    }

    const topCollider = this.colliders.find(
      x => x.label.toLowerCase().includes('topleft') || x.label.toLowerCase().includes('topcenter')
    )
    if (topCollider) {
      this.#escapeCoinBounds.minY = topCollider.bounds.min.y
    }
  }

  updateColliderPositions() {}

  updateTopGroupPosition(y: number) {}

  testSwitchSensors() {
    if (this.switchSensors.length === 0) {
      return
    }

    this.switchSensors.forEach(switchSensor => {
      let collides = false
      this.bodies
        //.filter(b => !this.interactions.draggedCoins.includes(b))
        .forEach(body => {
          if (Bounds.overlaps(body.bounds, switchSensor.bounds)) {
            collides = true
          }
        })

      if (
        !this.#switchSensorStatus.has(switchSensor.label) ||
        this.#switchSensorStatus.get(switchSensor.label) !== collides
      ) {
        if (collides) {
          this.#switchSensorStatus.set(switchSensor.label, true)
          this.onSwitchSensorOn.invoke(switchSensor.label)
        } else {
          this.#switchSensorStatus.set(switchSensor.label, false)
          this.onSwitchSensorOff.invoke(switchSensor.label)
        }
      }
    })
  }

  resize() {
    tempVector2.set(-1, -1)
    const corner = SceneUtils.screenToSceneSpace(this.camera, tempVector2, PhysicsLayerZ.Front)
    corner.multiplyScalar(100)

    this.visibleEdge.set(corner.x, corner.y)

    if (this.#renderCanvas) {
      const size = container.resolve<ISize>(Interface.ISize).getSize()
      this.#renderCanvas.width = size.width * 0.5
      this.#renderCanvas.height = size.height * 0.5
      this.updateRenderViewport()
    }

    this.updateEscapeBounds()
    this.updateColliderPositions()
  }
}
